import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { SettingsIcon } from "lucide-react";
import { FiscalDocumentData } from "../../types/fiscalDocument.type";
import { useBillingFiscalDocumentCancel } from "../../hooks/useBillingFiscalDocumentCancel";
import { useBillingFiscalDocumentUncancel } from "../../hooks/useBillingFiscalDocumentUncancel";
import { formatDateTime } from "@/common/utils/formatters";

interface FiscalDocumentChangeStatusModalProps {
  fiscalDocument: FiscalDocumentData;
}

const FiscalDocumentChangeStatusModal = ({ fiscalDocument }: FiscalDocumentChangeStatusModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { mutate: cancelFiscalDocument } = useBillingFiscalDocumentCancel({
    fiscalDocumentId: fiscalDocument.id,
    onSuccess: () => {
      setIsError(false);
      setMessages(['Nota fiscal cancelada com sucesso!']);
    },
    onError: () => {
      setIsError(true);
      setMessages(['Ocorreu um erro ao cancelar a nota fiscal.']);
    },
  });

  const { mutate: uncancelFiscalDocument } = useBillingFiscalDocumentUncancel({
    fiscalDocumentId: fiscalDocument.id,
    onSuccess: () => {
      setIsError(false);
      setMessages(['Nota fiscal descancelada com sucesso!']);
    },
    onError: () => {
      setIsError(true);
      setMessages(['Ocorreu um erro ao descancelar a nota fiscal.']);
    },
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onConfirm={() => setIsOpen(false)}
        Trigger={<SettingsIcon strokeWidth={1} />}
        title="Alterar status de NF"
        footer="Fechar"
        body={(
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3" data-testid="fiscal_documents_fields_section">
              <div>
                <p className="text-[14px] font-light">Número da NF</p>
                <p className="text-[16px] text-2xl text-muted">{fiscalDocument.number}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Autor</p>
                <p className="text-[16px] text-2xl text-muted">{fiscalDocument.uploaded_by?.full_name ?? '--'}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Número serial</p>
                <p className="text-[16px] text-2xl text-muted">{fiscalDocument.serial}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Data de emissão</p>
                <p className="text-[16px] text-2xl text-muted">{fiscalDocument.issued_at ? formatDateTime(new Date(fiscalDocument.issued_at)) : '--'}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Motivo da falha</p>
                <p className="text-[16px] text-2xl text-muted">{fiscalDocument.failure_reason ? fiscalDocument.failure_reason : '--'}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {fiscalDocument.canceled_at ? (
                <>
                  <p className="text-white-800">Nota fiscal cancelada em {formatDateTime(new Date(fiscalDocument.canceled_at))}</p>

                  <Button
                    onClick={() => uncancelFiscalDocument()}
                    variant="yellow"
                    className="text-white-800"
                  >
                    Descancelar NF
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => cancelFiscalDocument()}
                  variant="error"
                  className="text-white-800"
                >
                  Cancelar NF
                </Button>
              )}
            </div>
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingBillFiscalDocuments'] });
          }}
        />
      )}
    </>
  );
};

export default FiscalDocumentChangeStatusModal;
